/**
 * Firebase Client SDK Configuration
 *
 * Initializes Firebase for the frontend application.
 * Falls back to demo mode when credentials are not configured.
 */

import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator, httpsCallable } from 'firebase/functions';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Check if we're in demo mode (no Firebase credentials)
export const isDemoMode = !firebaseConfig.apiKey || firebaseConfig.apiKey === 'undefined';

// Initialize Firebase only if credentials are available
let app = null;
if (!isDemoMode) {
  app = initializeApp(firebaseConfig);
}

// Initialize services (null in demo mode)
export const auth = isDemoMode ? null : getAuth(app);
export const db = isDemoMode ? null : getFirestore(app);
export const functions = isDemoMode ? null : getFunctions(app, 'us-central1');
export const storage = isDemoMode ? null : getStorage(app);

// Connect to emulators in development
if (!isDemoMode && process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_EMULATORS === 'true') {
  console.log('Connecting to Firebase emulators...');
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectFunctionsEmulator(functions, 'localhost', 5001);
  connectStorageEmulator(storage, 'localhost', 9199);
}

if (isDemoMode) {
  console.log('🎭 Running in DEMO MODE - Using mock data');
}

/**
 * Create a callable function wrapper
 * @param {string} name - Function name
 * @returns {Function} Callable function (or null in demo mode)
 */
export function createCallable(name) {
  if (isDemoMode || !functions) {
    return null;
  }
  return httpsCallable(functions, name);
}

// Pre-configured callable functions
export const callables = {
  // Employee functions
  manageEmployees: createCallable('manage_employees'),

  // Leave functions
  manageLeave: createCallable('manage_leave'),

  // Payroll functions
  managePayroll: createCallable('manage_payroll'),

  // Report functions
  generateReport: createCallable('generate_report'),

  // Policy functions
  generatePolicy: createCallable('generate_policy'),

  // Forecast functions
  getForecast: createCallable('get_forecast'),

  // Chat functions
  chat: createCallable('chat'),

  // Dashboard functions
  dashboard: createCallable('dashboard'),

  // Config functions
  getConfig: createCallable('get_config'),
  updateConfig: createCallable('update_config'),
};

export default app;
